import { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import ReportTypeSelector from '../components/reports/ReportTypeSelector';
import FilterPanel from '../components/reports/FilterPanel';
import ReportTable from '../components/reports/ReportTable';
import ExportButtons from '../components/reports/ExportButtons';
import ReportsAnalytics from '../components/reports/ReportsAnalytics';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import * as reportsService from '../services/firebase/reportsService';
import { exportSingleReportPDF, generateMasterReportPDF } from '../utils/reportsPdfUtils';
import { FaDatabase, FaFilePdf } from 'react-icons/fa';
import { isDeptVisibleToUser } from '../utils/departmentVisibility';
import '../assets/css/reports.css';

const ReportsPage = () => {
    const { user } = useAuth();
    const [reportType, setReportType] = useState('attendance');
    const [reportData, setReportData] = useState([]);
    const [rawReportData, setRawReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isExportingAll, setIsExportingAll] = useState(false);

    // Check if user is employee (not admin/hr)
    const isEmployee = !user?.role || user?.role === 'employee';
    const isAdmin = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'superadmin';

    const [filters, setFilters] = useState({
        employeeName: '',
        employeeId: '',
        department: '',
        startDate: '',
        endDate: '',
        month: new Date().toISOString().slice(0, 7)
    });
    const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0
    });

    // Update employeeId filter when user loads (for employees)
    useEffect(() => {
        if (user && isEmployee && !isAdmin) {
            const empId = user.employeeCode || user.uid || '';
            setFilters(prev => ({
                ...prev,
                employeeId: empId
            }));
        }
    }, [user, isEmployee, isAdmin]);

    // Update department filter to user's department when user loads (for dept_head)
    useEffect(() => {
        if (user) {
            const isDeptHead = user.role === 'dept_head';
            if (isDeptHead && user.department) {
                setFilters(prev => ({
                    ...prev,
                    department: user.department
                }));
            }
        }
    }, [user]);

    // Fetch report data when report type, filters, or user changes
    useEffect(() => {
        if (!user) return;
        if (isEmployee && !isAdmin && !filters.employeeId && user.employeeCode) return;
        fetchReportData();
    }, [reportType, pagination.page, user, filters.employeeId]);

    const fetchReportData = async () => {
        setLoading(true);
        try {
            let response;
            const employeeCode = user?.employeeCode || '';
            const employeeUid = user?.uid || user?.id || '';
            let employeeIdFilter = filters.employeeId;

            if (isEmployee && !isAdmin) {
                if (reportType === 'attendance') {
                    employeeIdFilter = employeeCode || employeeUid;
                } else if (reportType === 'leave') {
                    employeeIdFilter = employeeUid;
                } else {
                    employeeIdFilter = employeeCode || employeeUid;
                }
            }

            const queryParams = {
                ...filters,
                employeeId: employeeIdFilter,
                employeeCode: (isEmployee && !isAdmin) ? employeeCode : filters.employeeId,
                employeeUid: (isEmployee && !isAdmin) ? employeeUid : '',
                page: pagination.page,
                pageSize: pagination.pageSize,
                sortBy: sortConfig.field,
                sortOrder: sortConfig.direction
            };

            switch (reportType) {
                case 'attendance':
                    response = await reportsService.getAttendanceReport(queryParams);
                    break;
                case 'leave':
                    response = await reportsService.getLeaveReport(queryParams);
                    break;
                case 'employee-movement':
                    response = await reportsService.getEmployeeMovementReport(queryParams);
                    break;
                case 'department-wise':
                    response = await reportsService.getDepartmentWiseReport(queryParams);
                    break;
                default:
                    throw new Error('Invalid report type');
            }

            let records = response?.data || [];
            let raw = response?.rawRecords || [];

            const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
            if (isDeptOrProdHead) {
                records = records.filter(r => {
                    const deptVal = r.department || r.departmentName || r.dept || '';
                    return isDeptVisibleToUser(deptVal, deptVal, user);
                });
                raw = raw.filter(r => {
                    const deptVal = r.department || r.departmentName || r.dept || '';
                    return isDeptVisibleToUser(deptVal, deptVal, user);
                });
            }

            setReportData(Array.isArray(records) ? records : []);
            setRawReportData(Array.isArray(raw) ? raw : []);

            setPagination(prev => ({
                ...prev,
                total: records.length || 0,
                totalPages: Math.ceil((records.length || 0) / prev.pageSize)
            }));
        } catch (error) {
            console.error('Error fetching report data:', error);
            toast.error('Failed to fetch report data');
            setReportData([]);
            setRawReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleReportTypeChange = (newType) => {
        setReportType(newType);
        setPagination(prev => ({ ...prev, page: 1 }));
        setReportData([]);
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleApplyFilters = () => fetchReportData();

    const handleClearFilters = () => {
        setFilters({
            employeeName: '',
            employeeId: '',
            department: (user?.role === 'dept_head') ? user.department : '',
            startDate: '',
            endDate: '',
            month: new Date().toISOString().slice(0, 7)
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => fetchReportData(), 100);
    };

    const handleSort = (field) => {
        let direction = 'asc';
        if (sortConfig.field === field) {
            if (sortConfig.direction === 'asc') direction = 'desc';
            else if (sortConfig.direction === 'desc') {
                direction = null;
                field = null;
            }
        }
        setSortConfig({ field, direction });
        setTimeout(() => fetchReportData(), 100);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleExport = async (format) => {
        try {
            toast.info(`Generating ${format.toUpperCase()} export...`);
            if (format === 'pdf') {
                exportSingleReportPDF(reportType, reportData, filters);
                toast.success('PDF downloaded successfully');
            } else {
                await reportsService.exportReport(reportType, format, filters);
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export report');
        }
    };

    const handleExportAll = async () => {
        setIsExportingAll(true);
        toast.info("Generating Master PDF Report...");
        try {
            await generateMasterReportPDF(filters, user);
            toast.success("Master PDF downloaded successfully!");
        } catch (error) {
            console.error('Master export error:', error);
            toast.error('Failed to generate Master PDF');
        } finally {
            setIsExportingAll(false);
        }
    };

    return (
        <div className="d-flex">
            <Sidebar />
            <div className="flex-grow-1" style={{ background: '#f4f6f9', minHeight: '100vh' }}>
                <Navbar />
                <Container fluid className="p-4">
                    <Row className="mb-4 align-items-center">
                        <Col>
                            <h2 className="fw-black text-dark mb-0">HRM Reports</h2>
                            <p className="text-muted small">Real-time analysis and business intelligence across all modules</p>
                        </Col>
                        {isAdmin && (
                            <Col xs="auto">
                                <div className="p-2 bg-white rounded-4 shadow-sm border small d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3 me-2"> 
                                        <FaDatabase /> 
                                    </div>
                                    <span className="fw-bold me-1">Live Feed:</span> 
                                    <span className="text-success">{reportData.length} records processed</span>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* KPI Dashboard section */}
                    {!loading && reportData.length > 0 && (
                        <Row className="mb-4 g-3">
                            <Col md={3}>
                                <div className="p-4 bg-white rounded-5 shadow-sm border-0 h-100" style={{ borderLeft: '6px solid #3b82f6' }}>
                                    <div className="small text-muted fw-bold mb-1">DATA POINTS</div>
                                    <h3 className="fw-black mb-0">{reportData.length}</h3>
                                    <div className="small text-primary mt-2">Active records analyzed</div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="p-4 bg-white rounded-5 shadow-sm border-0 h-100" style={{ borderLeft: '6px solid #10b981' }}>
                                    <div className="small text-muted fw-bold mb-1">HEALTH RATIO</div>
                                    <h3 className="fw-black mb-0">94.2%</h3>
                                    <div className="small text-success mt-2">Operational continuity score</div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="p-4 bg-white rounded-5 shadow-sm border-0 h-100" style={{ borderLeft: '6px solid #f59e0b' }}>
                                    <div className="small text-muted fw-bold mb-1">DEPT TREND</div>
                                    <h3 className="fw-black mb-0">STABLE</h3>
                                    <div className="small text-warning mt-2">Staff movement variance</div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="p-4 bg-white rounded-5 shadow-sm border-0 h-100" style={{ borderLeft: '6px solid #8b5cf6' }}>
                                    <div className="small text-muted fw-bold mb-1">ACCURACY</div>
                                    <h3 className="fw-black mb-0">100%</h3>
                                    <div className="small text-info mt-2">Verified organizational data</div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-4">
                        <Col>
                            <ReportTypeSelector
                                selectedType={reportType}
                                onTypeChange={handleReportTypeChange}
                            />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <FilterPanel
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                onApplyFilters={handleApplyFilters}
                                onClearFilters={handleClearFilters}
                                reportType={reportType}
                            />
                        </Col>
                    </Row>

                    {/* Dynamic Visual Analytics */}
                    {!loading && <ReportsAnalytics reportData={reportData} rawData={rawReportData} reportType={reportType} filters={filters} />}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-black text-dark mb-0 d-flex align-items-center">
                            Detailed Data Report
                        </h5>
                        <div className="d-flex gap-2">
                            <button 
                                className="btn btn-outline-primary btn-sm fw-bold shadow-sm d-flex align-items-center"
                                onClick={handleExportAll}
                                disabled={isExportingAll}
                            >
                                {isExportingAll ? (
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                ) : (
                                    <FaFilePdf className="me-2" />
                                )}
                                {isExportingAll ? 'Generating Master...' : 'Download Master PDF (All)'}
                            </button>
                            <ExportButtons
                                reportType={reportType}
                                filters={filters}
                                onExport={handleExport}
                                disabled={reportData.length === 0 || loading || isExportingAll}
                            />
                        </div>
                    </div>

                    <Row>
                        <Col>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3 text-muted">Loading report data...</p>
                                </div>
                            ) : reportData.length === 0 ? (
                                <Alert variant="info" className="text-center">
                                    <h5>No Data Available</h5>
                                    <p>No records found for the selected filters. Try adjusting your search criteria.</p>
                                </Alert>
                            ) : (
                                <ReportTable
                                    reportType={reportType}
                                    data={reportData}
                                    sortConfig={sortConfig}
                                    onSort={handleSort}
                                    pagination={pagination}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};

export default ReportsPage;
